/**
 * fileStatusStore - Zustand store for SSE-based file processing status.
 *
 * Manages a single SSE connection to /api/files/processing-updates that
 * streams progress for all files being processed.
 *
 * Design principles:
 * - Single SSE connection for all files (not per-file)
 * - SSE stays connected while uploads OR processing files exist
 * - Automatic reconnection with exponential backoff on errors
 * - Connection lifecycle controlled externally by useSSELifecycle
 *
 * Used by:
 * - PhaseRing: Displays processing phase progress
 * - useSSELifecycle: Manages subscription based on upload/processing state
 */

import { create } from 'zustand';
import { useEffect, useRef } from 'react';
import { getApiBaseUrl } from '../services/api';
import { auth } from '../firebase';
import { useUploadProgressStore } from './uploadProgressStore';
import { fileStem } from '../utils/filename';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Unified file status (January 2026 schema) */
export type FileStatus = 'ingesting' | 'complete' | 'failed';
export type UnitType = 'sheets' | 'pages' | 'slides';

export interface ProcessingStatus {
  status: FileStatus;
  unitsTotal: number;
  unitsIngested: number;
  unitType: UnitType;
  isQueryable: boolean;
  error?: string;
}

/** File metadata from SSE event — enough to add to the tree */
export interface SSEFileInfo {
  fileId: string;
  name: string;
  /** The file's one name, with its extension — the tree row needs it. */
  filename: string;
  fileType: string;
  folderId: string | null;
  projectId: string | null;
  /** The agent whose file it is — which agent's tree it joins. */
  agentId: string | null;
}

interface FileStatusState {
  /** The server's `file_type` per file, as the processing-updates stream last
   *  reported it. A tree row is created with the name's provisional kind; the
   *  ingest decides from the bytes, and `ProjectContext` follows this map. */
  fileTypes: Record<string, string>;
  /** Processing status keyed by fileId */
  statuses: Record<string, ProcessingStatus>;

  /** Files discovered via SSE that aren't in the tree yet */
  pendingFiles: SSEFileInfo[];

  /** Active connection (abort controller wrapper) */
  connection: { abort: () => void } | null;

  /** Current reconnect attempt (for backoff) */
  reconnectAttempt: number;

  /** Connection state for UI feedback */
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error';

  /** Callback for sync_updated events (set by useSSELifecycle) */
  _onSyncUpdated: (() => void) | null;
}

interface FileStatusActions {
  /** Subscribe to SSE stream */
  subscribe: () => Promise<void>;

  /** Unsubscribe from SSE stream */
  unsubscribe: () => void;

  /** Clear status for a specific file */
  clearStatus: (fileId: string) => void;

  /** Get status for a specific file */
  getStatus: (fileId: string) => ProcessingStatus | undefined;

  /** Seed statuses from a project tree's rows (for page refresh recovery) */
  seedStatuses: (files: Array<{ fileId: string; status: ProcessingStatus }>) => void;

  /** Clear pending files after they've been added to the tree */
  clearPendingFiles: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if any files are still processing (not complete/failed).
 * Used to determine if SSE connection should stay open.
 */
function hasFilesProcessing(statuses: Record<string, ProcessingStatus>): boolean {
  return Object.values(statuses).some(
    (s) => s.status === 'ingesting'
  );
}

/**
 * Count files that are still processing.
 * Used by useSSELifecycle to track processing state changes.
 */
function countFilesProcessing(statuses: Record<string, ProcessingStatus>): number {
  return Object.values(statuses).filter(
    (s) => s.status === 'ingesting'
  ).length;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useFileStatusStore = create<FileStatusState & FileStatusActions>((set, get) => ({
  statuses: {},
  fileTypes: {},
  pendingFiles: [],
  connection: null,
  reconnectAttempt: 0,
  connectionState: 'disconnected',
  _onSyncUpdated: null,

  subscribe: async () => {
    // Don't connect if already connected
    if (get().connection) return;

    set({ connectionState: 'connecting' });

    // Get auth token
    const user = auth.currentUser;
    if (!user) {
      console.warn('[fileStatusStore] No auth user, cannot subscribe to SSE');
      set({ connectionState: 'disconnected' });
      return;
    }

    let token: string;
    try {
      token = await user.getIdToken();
    } catch (e) {
      console.error('[fileStatusStore] Failed to get auth token:', e);
      set({ connectionState: 'error' });
      return;
    }

    const controller = new AbortController();
    set({ connection: { abort: () => controller.abort() } });

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/files/processing-updates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      set({ connectionState: 'connected', reconnectAttempt: 0 });

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              // Skip idle events
              if (data.type === 'idle') continue;

              // Handle error events
              if (data.type === 'error') {
                console.error('[fileStatusStore] Server error:', data.message);
                continue;
              }

              // Handle sync_updated events (folder contents changed)
              if (data.type === 'sync_updated') {
                get()._onSyncUpdated?.();
                continue;
              }

              // Update status keyed by file_id (matches Sidebar, PhaseRing, uploadProgressStore)
              const fileId = data.file_id;
              if (!fileId) continue;

              // If this file_id is new (not in statuses and has metadata),
              // queue it for the React layer to add to the tree
              const isNew = !get().statuses[fileId] && !useUploadProgressStore.getState().states[fileId];
              if (isNew && data.filename && data.file_type) {
                set((state) => ({
                  pendingFiles: [
                    ...state.pendingFiles,
                    {
                      fileId,
                      name: fileStem(data.filename),
                      filename: data.filename,
                      fileType: data.file_type,
                      folderId: data.folder_id || null,
                      projectId: data.project_id || null,
                      agentId: data.agent_id || null,
                    },
                  ],
                }));
              }

              set((state) => ({
                statuses: {
                  ...state.statuses,
                  [fileId]: {
                    status: data.status || 'ingesting',
                    unitsTotal: data.units_total || 0,
                    unitsIngested: data.units_ingested || 0,
                    unitType: data.unit_type || 'sheets',
                    isQueryable: data.is_queryable || false,
                    error: data.error,
                  },
                },
                // The row's file_type as the server now holds it. The tree
                // row was created with the name's provisional kind; the ingest
                // decided from the bytes, and this is how the tree learns.
                fileTypes: data.file_type && state.fileTypes[fileId] !== data.file_type
                  ? { ...state.fileTypes, [fileId]: data.file_type }
                  : state.fileTypes,
              }));

              // Clear upload tracking when processing has started or completed
              const fileStatus = data.status;
              if (fileStatus === 'complete' || data.is_queryable) {
                useUploadProgressStore.getState().clearUpload(fileId);
              }

              // Auto-clear completed status after brief delay so users see completion
              // Ring disappears when status changes to 'complete', checkmark follows shortly after
              if (fileStatus === 'complete') {
                setTimeout(() => {
                  get().clearStatus(fileId);
                }, 300);
              }
            } catch (e) {
              console.warn('[fileStatusStore] Failed to parse SSE event:', e);
            }
          }
        }
      }

      // Connection closed normally - clean up
      set({ connection: null, connectionState: 'disconnected' });

      // Reconnect if there are still uploads or files processing
      const hasUploads = Object.keys(useUploadProgressStore.getState().states).length > 0;
      const hasProcessing = hasFilesProcessing(get().statuses);
      if (hasUploads || hasProcessing) {
        setTimeout(() => get().subscribe(), 500);
      }
    } catch (e) {
      // Clean up connection state
      set({ connection: null });

      if ((e as Error).name === 'AbortError') {
        // Expected when unsubscribing
        set({ connectionState: 'disconnected' });
        return;
      }

      console.error('[fileStatusStore] SSE connection error:', e);
      set({ connectionState: 'error' });

      // Reconnect with backoff if there are still uploads or files processing
      const hasUploads = Object.keys(useUploadProgressStore.getState().states).length > 0;
      const hasProcessing = hasFilesProcessing(get().statuses);
      if (hasUploads || hasProcessing) {
        const attempt = get().reconnectAttempt;
        const delay = Math.min(500 * Math.pow(2, attempt), 5000);
        set({ reconnectAttempt: attempt + 1 });

        setTimeout(() => {
          if (!get().connection) {
            get().subscribe();
          }
        }, delay);
      }
    }
  },

  unsubscribe: () => {
    const conn = get().connection;
    if (conn) {
      conn.abort();
    }
    set({ connection: null, reconnectAttempt: 0, connectionState: 'disconnected' });
  },

  clearStatus: (fileId) => set((state) => {
    const { [fileId]: _, ...rest } = state.statuses;
    return { statuses: rest };
  }),

  getStatus: (fileId) => get().statuses[fileId],

  clearPendingFiles: () => set({ pendingFiles: [] }),

  seedStatuses: (files) => {
    const newStatuses: Record<string, ProcessingStatus> = {};
    for (const { fileId, status } of files) {
      // Only seed files that are still processing
      if (status.status === 'ingesting') {
        newStatuses[fileId] = status;
      }
    }

    if (Object.keys(newStatuses).length > 0) {
      set((state) => ({
        statuses: { ...state.statuses, ...newStatuses },
      }));
      // Auto-subscribe to get updates for these seeded files
      get().subscribe();
    }
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Selector hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useFileStatus(fileId: string) {
  return useFileStatusStore((state) => state.statuses[fileId]);
}

export function useSSEConnectionState() {
  return useFileStatusStore((state) => state.connectionState);
}

// ─────────────────────────────────────────────────────────────────────────────
// SSE Lifecycle Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to manage SSE subscription lifecycle.
 *
 * SSE stays connected while:
 * - There are uploads in progress (uploadProgressStore), OR
 * - There are files still processing (fileStatusStore)
 *
 * This ensures we receive all progress updates even after the XHR upload completes
 * but before processing finishes.
 *
 * Call this hook once at the app level.
 */
export function useSSELifecycle(
  addOptimisticItem?: (item: any) => void,
  hasSyncFolders?: boolean,
  onSyncUpdated?: () => Promise<void>,
) {
  const uploadCount = useUploadProgressStore((state) => Object.keys(state.states).length);
  const processingCount = useFileStatusStore((state) => countFilesProcessing(state.statuses));
  const pendingFiles = useFileStatusStore((state) => state.pendingFiles);

  // Track whether SSE should be connected: uploads, processing, or sync folders
  const needsConnection = uploadCount > 0 || processingCount > 0 || !!hasSyncFolders;
  const prevNeedsConnectionRef = useRef(needsConnection);

  useEffect(() => {
    const prevNeedsConnection = prevNeedsConnectionRef.current;
    prevNeedsConnectionRef.current = needsConnection;

    if (needsConnection && !prevNeedsConnection) {
      // Started needing connection - connect
      useFileStatusStore.getState().subscribe();
    } else if (!needsConnection && prevNeedsConnection) {
      // No longer need connection - disconnect
      useFileStatusStore.getState().unsubscribe();
    }
  }, [needsConnection]);

  // Set sync_updated callback so the SSE handler can trigger tree refresh
  useEffect(() => {
    if (onSyncUpdated) {
      useFileStatusStore.setState({ _onSyncUpdated: onSyncUpdated });
      return () => { useFileStatusStore.setState({ _onSyncUpdated: null }); };
    }
  }, [onSyncUpdated]);

  // Add newly discovered files to the tree
  useEffect(() => {
    if (pendingFiles.length > 0 && addOptimisticItem) {
      for (const f of pendingFiles) {
        addOptimisticItem({
          id: f.fileId,
          name: f.name,
          filename: f.filename,
          type: 'file' as const,
          file_type: f.fileType,
          parentId: f.folderId,
          agentId: f.agentId,
          children: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      useFileStatusStore.getState().clearPendingFiles();
    }
  }, [pendingFiles, addOptimisticItem]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      useFileStatusStore.getState().unsubscribe();
    };
  }, []);
}
